// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * G4-2-rotation-timer — web-side rotation orchestrator.
 *
 * The Rust core (`crates/lakko-channels/src/rotation_policy.rs`)
 * decides _when_ to rotate via a pure
 * `channelShouldRotate(policy, state, nowMs)` function. The
 * orchestrator here decides _how_: it walks the subscriber
 * list, reseals the new content seed for each subscriber via
 * the wasm `sealSubscriberSeedEnvelope` surface, then fans the
 * envelopes out with the ADR-0030 chunked staged-rotation
 * protocol — `POST /rotate/stage` in bounded chunks (at most
 * `MAX_STAGE_CHUNK_ENTRIES` each, resumable + idempotent),
 * followed by one atomic `POST /rotate/commit` that promotes
 * the staged set + bumps the epoch. The commit is the single
 * atomic point (C-42): a subscriber never observes a torn
 * rotation — it sees the old epoch until commit lands, then the
 * new epoch with every staged envelope already live.
 *
 * Two entry points:
 *
 *   - `runRotationOnce(...)` — single-pass; returns the new
 *     rotation state. Use this from a "Rotate now" button.
 *   - `startRotationCron(...)` — periodic poller. Calls
 *     `runRotationOnce` on every tick, persists state via
 *     `saveState`, surfaces errors via `onError`. Returns
 *     a handle whose `.stop()` cancels the timer.
 *
 * The orchestrator is **idempotent under no-op** — if the
 * policy says "no rotation needed", it returns the input
 * state unchanged + does no network round-trip.
 *
 * **Failure semantics:**
 *
 *   - If `listSubscribers` / any `stageRotation` chunk /
 *     `commitRotation` throws, the state is NOT updated (so the
 *     next tick retries). Staging is an idempotent upsert, so a
 *     retry re-stages the already-staged chunks harmlessly
 *     before reaching the ones that were missed — no torn
 *     rotation is observable, because the epoch is only bumped
 *     by the final atomic commit.
 *   - If a single envelope seal fails (e.g. malformed
 *     subscriber pubkey hex), the WHOLE batch is aborted before
 *     any chunk is staged — same atomic posture the server
 *     enforces. Partial fanouts would leave some subscribers on
 *     the old seed + some on the new, which is worse than
 *     retrying.
 *   - G-50: the rotation's effects (the DS epoch registration,
 *     the reseal + commit) are recorded durably WITH the N+1
 *     provider snapshot before any of them runs
 *     (`publisher-commit-outbox.ts`); a reload or a failed step
 *     resumes them from that record, never from a fresh fetch of
 *     the server epoch.
 *   - If the fanout (stage + commit) succeeds but the local
 *     `saveState` callback throws, the rotation is still
 *     committed server-side; the caller's storage is in a
 *     stale state and the next tick will trigger another
 *     rotation. That's a deliberate trade-off: we cannot
 *     un-commit the server, but we MUST persist the new
 *     state to avoid an infinite rotation loop.
 *
 * The wasm surface is loaded lazily by the caller — this
 * module takes the wasm `PublisherGroup` + `Identity`
 * handles as inputs so it never has to await
 * `loadWasm()` itself. Web app callers already hold both
 * handles after `Channels.tsx` opens or creates the
 * PublisherGroup.
 */

import type { Wasm } from "../wasm";
import { loadWasm } from "../wasm";
import { DsClient } from "../ds/client";
import { commitWithNextEpoch, takeSigner, type ProofSigner } from "../ds/access";
import { isCommitConflict } from "../ds/ownership";
import { isBackoffRefusal } from "../chat/sweep-backoff";
import { channelsActions } from "../state/channels-state";
import {
  newEntryId,
  runPublisherCommit,
  type PublisherCommitEntry,
  type PublisherCommitPorts,
} from "./publisher-commit-outbox";

type Identity = InstanceType<Wasm["Identity"]>;
type PublisherGroup = InstanceType<Wasm["PublisherGroup"]>;
type ChannelRotationPolicy = ReturnType<Wasm["channelRotationPolicyDefault"]>;
type ChannelRotationState = ReturnType<Wasm["channelRotationStateNew"]>;

import {
  commitRotation,
  fetchEpoch,
  listSubscribers,
  stageRotation,
  MAX_STAGE_CHUNK_ENTRIES,
  type ChannelEpoch,
  type RotateBatchEntry,
} from "./api";
import { sealEachSkippingInvalid } from "./member-card-rules";

/**
 * Caller-provided handles + persistence callbacks. Kept as
 * one object so the call site cannot forget a field on a
 * `runRotationOnce` invocation.
 */
export interface RotationContext {
  /** Wasm PublisherGroup handle — the publisher's MLS group
   *  for this channel. */
  readonly publisherGroup: PublisherGroup;
  /** Wasm Identity handle — the publisher's MLS identity
   *  + signing key. Required by `rotate` and `channelSeed`. */
  readonly identity: Identity;
  /** Hex-encoded 16-byte channel id (used in URLs). */
  readonly channelIdHex: string;
  /** Optional API base override (defaults to the channels
   *  api's `DEFAULT_BASE`). */
  readonly apiBase?: string;
  /** Caller-persistence: invoked synchronously inside
   *  `runRotationOnce` with the new state after a successful
   *  fanout. Implementation should write to IndexedDB /
   *  localStorage / whatever the channel record lives in.
   *  If this throws, the rotation has already been committed
   *  server-side — see "Failure semantics" above. */
  readonly saveState: (next: ChannelRotationState) => void | Promise<void>;
  /** Optional callback after a successful rotation,
   *  carrying the new server-side epoch. UI can refresh
   *  the displayed epoch + reload subscriber envelopes. */
  readonly onRotated?: (newEpoch: number) => void;
}

/** Result of one single-pass rotation attempt. */
export interface RotationOnceResult {
  /** `true` if the policy fired AND the rotation
   *  completed end-to-end (PublisherGroup rotated +
   *  envelopes sealed + server committed atomically). */
  readonly rotated: boolean;
  /** New rotation state to persist. For `rotated:false`
   *  cases this is byte-identical to the input state. */
  readonly newState: ChannelRotationState;
  /** New server-side epoch id after the rotation, when
   *  `rotated:true`. Undefined otherwise. */
  readonly newEpoch?: number;
}

/**
 * Run a single rotation pass. Returns `{ rotated:false }`
 * if the policy says no; otherwise drives the full chain.
 *
 * This does NOT mutate any state on the caller side — the
 * caller MUST persist `result.newState` via
 * `ctx.saveState` (which `startRotationCron` does for you).
 */
export async function runRotationOnce(
  ctx: RotationContext,
  policy: ChannelRotationPolicy,
  state: ChannelRotationState,
  nowMs: number = Date.now(),
): Promise<RotationOnceResult> {
  const nowMsBig = BigInt(Math.max(0, Math.floor(nowMs)));
  const wasm = await loadWasm();
  if (!wasm.channelShouldRotate(policy, state, nowMsBig)) {
    return { rotated: false, newState: state };
  }

  // 1. The server epoch the envelopes will bind to, read BEFORE the local
  //    rotation: after the eager merge nothing may fail before the commit's
  //    record is durable (G-50), and this is a network read.
  const current = await fetchEpoch(ctx.channelIdHex, ctx.apiBase);
  const newServerEpoch = current.epoch + 1;
  if (!Number.isInteger(newServerEpoch) || newServerEpoch < 0) {
    throw new Error(`unexpected server epoch ${current.epoch}; cannot derive new epoch`);
  }

  // 2. Advance the local MLS epoch (the self-update commit eager-merges
  //    N -> N+1 in the account identity's provider), then record the
  //    rotation together with that provider snapshot, durably, before any
  //    of its steps runs. A refused record runs nothing.
  // ADR-0081 - the key of the epoch the rotation leaves proves its commit
  // request; its seed is taken before the eager merge and recorded with the
  // rotation, so a replay after a reload can still prove it.
  const proofEpoch = ctx.publisherGroup.epoch();
  const proofSeed = ctx.publisherGroup.dsAccessSeed(ctx.identity);
  const rotationCommit = ctx.publisherGroup.rotate(ctx.identity);
  const entry: PublisherCommitEntry = {
    id: newEntryId(),
    kind: "rotation",
    channelIdHex: ctx.channelIdHex,
    groupIdHex: hexOf(ctx.publisherGroup.groupId()),
    targetEpoch: ctx.publisherGroup.epoch().toString(),
    serverTargetEpoch: newServerEpoch,
    commitB64: b64Of(rotationCommit),
    welcomeB64: null,
    joinerKeyB64: null,
    replyGrantB64: null,
    proofEpoch: proofEpoch.toString(),
    proofSeedB64: b64Of(proofSeed),
    journalLost: false,
    done: [],
  };
  proofSeed.fill(0);
  await channelsActions.recordPublisherCommit(entry);

  // 3. The steps: the N+1 epoch key and the rotation commit on the DS in
  //    one request (ADR-0057, ADR-0081), reseal the N+1 seed to every
  //    subscriber and commit the rotation on the channels service (ADR-0030
  //    chunked fanout).
  await runPublisherCommit(entry, publisherCommitPorts(ctx));

  // 4. Stamp the new state + persist.
  const newState = wasm.channelRotationStateAfterRotation(nowMsBig);
  await ctx.saveState(newState);
  ctx.onRotated?.(newServerEpoch);

  return { rotated: true, newState, newEpoch: newServerEpoch };
}

/** What a PublisherGroup commit's steps run against. */
export interface PublisherCommitContext {
  readonly publisherGroup: PublisherGroup;
  readonly identity: Identity;
  readonly channelIdHex: string;
  readonly apiBase?: string;
  readonly ds?: DsClient;
}

/**
 * G-50 — the ports a recorded PublisherGroup commit runs through. The
 * approve-only steps (the journal publish, the Welcome, the appointment)
 * come from the co-publisher module as `extras`; a rotation has none of
 * them. Recording goes to the channel log (`channelsActions`).
 */
export function publisherCommitPorts(
  ctx: PublisherCommitContext,
  extras: Partial<Pick<PublisherCommitPorts, "deliverWelcome" | "appoint">> = {},
): PublisherCommitPorts {
  const ds = ctx.ds ?? new DsClient();
  return {
    async commitToJournal(commit, proof) {
      const next = takeSigner(() => ctx.publisherGroup.dsAccessSigner(ctx.identity));
      // ADR-0081 - proven under the recorded key of the epoch the commit
      // left, live and on a replay alike. An entry without one (none is
      // written any more) can complete only an identical re-send, which the
      // DS recognises under any retained key.
      let proofSigner: ProofSigner | null = null;
      if (proof !== null) {
        const wasm = await loadWasm();
        proofSigner = takeSigner(() => wasm.DsProofSigner.forGroup(proof.epoch, proof.seed));
      }
      try {
        // ADR-0057 — registered with the channel owner's root, with the row.
        await commitWithNextEpoch(ds, ctx.publisherGroup.groupId(), next, proofSigner ?? next, commit);
      } finally {
        next?.free();
        proofSigner?.free();
      }
    },
    classifyCommitFailure: (err) => (isCommitConflict(err) || isBackoffRefusal(err) ? "lost" : "unknown"),
    deliverWelcome:
      extras.deliverWelcome ??
      (async () => {
        throw new Error("publisher commit: no Welcome for this kind");
      }),
    async serverEpoch() {
      return (await fetchEpoch(ctx.channelIdHex, ctx.apiBase)).epoch;
    },
    async reseal(targetEpoch) {
      await resealToSubscribers(ctx.publisherGroup, ctx.identity, ctx.channelIdHex, targetEpoch, ctx.apiBase);
    },
    appoint: extras.appoint ?? (async () => undefined),
    save: (e) => channelsActions.savePublisherCommit(e),
    finish: (e) => channelsActions.finishPublisherCommit(e.id),
  };
}

/**
 * Reseal the PublisherGroup's CURRENT seed to every subscriber, bound to
 * the channels-service epoch `targetEpoch`, and commit it there with the
 * ADR-0030 chunked fanout. A subscriber key the core refuses to seal to (a
 * pre-ADR-0060 X25519 key) is skipped; any other seal failure aborts the
 * whole batch.
 */
export async function resealToSubscribers(
  publisherGroup: PublisherGroup,
  identity: Identity,
  channelIdHex: string,
  targetEpoch: number,
  apiBase?: string,
): Promise<ChannelEpoch> {
  const wasm = await loadWasm();
  const newSeed = publisherGroup.channelSeed(identity);
  const channelIdBytes = publisherGroup.channelId();
  const subscribers = await listSubscribers(channelIdHex, identity, apiBase);
  const bytesByHex = new Map<string, Uint8Array>();
  for (const subscriberHex of subscribers.subscribers) {
    const subscriberBytes = hexToBytes(subscriberHex);
    if (subscriberBytes === null) {
      // Malformed subscriber hex from the server: refuse to post a partial
      // batch (the step stays owed and is retried).
      throw new Error(`subscriber pubkey hex is not valid: ${subscriberHex}`);
    }
    bytesByHex.set(subscriberHex, subscriberBytes);
  }
  // ADR-0060: a subscriber key is Ed25519 now. A key registered before the
  // flag day (an X25519 key) may not be a valid Ed25519 point, and the core
  // refuses to seal to it; that subscriber could not open the envelope
  // anyway, so it is skipped rather than aborting the whole rotation. Its
  // device re-registers a fresh key on its next open of the channel.
  const { sealed, skipped } = sealEachSkippingInvalid([...bytesByHex.keys()], (hex) =>
    wasm.sealSubscriberSeedEnvelope(bytesByHex.get(hex) ?? new Uint8Array(0), channelIdBytes, targetEpoch, newSeed),
  );
  if (skipped.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[channels] reseal skipped ${skipped.length} subscriber key(s) the core refused`);
  }
  const batch: RotateBatchEntry[] = sealed.map((s) => ({
    subscriber_pubkey_hex: s.subscriberHex,
    envelope_b64: base64Encode(s.value),
  }));
  return stageAndCommitRotation(channelIdHex, targetEpoch, batch, identity, apiBase);
}

function hexOf(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

/**
 * ADR-0030 chunked fanout: stage the sealed envelope batch in
 * bounded chunks (at most `MAX_STAGE_CHUNK_ENTRIES` each), then
 * run one atomic commit that promotes the staged set + bumps the
 * epoch. Returns the new epoch.
 *
 * Sequencing is deliberate:
 *
 *   - Chunks are staged SEQUENTIALLY. Staging is an idempotent
 *     upsert keyed on `(channel, target_epoch, subscriber)`, so
 *     a chunk failure throws and the caller does NOT advance its
 *     local rotation state — the next tick re-runs and re-stages
 *     the already-staged chunks harmlessly before reaching the
 *     ones that were missed (resumability).
 *   - Commit runs only after every chunk has staged. It is the
 *     single atomic point (C-42): subscribers see the old epoch
 *     until it lands, then the new epoch with every staged
 *     envelope already live — no torn rotation is observable,
 *     because the epoch is bumped nowhere but in the commit.
 *
 * An empty batch stages nothing and commits directly — this
 * still bumps the epoch (the "no subscribers yet" case where the
 * publisher keeps rotating on schedule before the audience
 * grows), matching the old single-shot `rotateChannel([])`.
 */
export async function stageAndCommitRotation(
  channelIdHex: string,
  targetEpoch: number,
  batch: ReadonlyArray<RotateBatchEntry>,
  identity: Identity,
  apiBase?: string,
): Promise<ChannelEpoch> {
  for (let i = 0; i < batch.length; i += MAX_STAGE_CHUNK_ENTRIES) {
    const chunk = batch.slice(i, i + MAX_STAGE_CHUNK_ENTRIES);
    // Sequential await — a chunk failure aborts here and the
    // caller's `saveState` never runs, so no partial rotation is
    // committed (commit below is never reached).
    await stageRotation(channelIdHex, targetEpoch, chunk, identity, apiBase);
  }
  return commitRotation(channelIdHex, targetEpoch, identity, apiBase);
}

/** Handle returned by `startRotationCron` — call `.stop()`
 *  to cancel the periodic timer. Safe to call multiple
 *  times (idempotent). */
export interface RotationCronHandle {
  stop(): void;
}

/** Options for `startRotationCron`. */
export interface RotationCronOptions {
  /** How often to poll the policy + (maybe) drive a
   *  rotation. Default 5 minutes — small enough that a
   *  24-hour interval rotation lands within ~5 min of the
   *  scheduled moment, large enough that an idle browser
   *  tab doesn't burn CPU on the policy check. */
  readonly pollIntervalMs?: number;
  /** Caller-supplied state reader. Invoked on every poll.
   *  Lets the cron pick up state changes written by other
   *  paths (e.g. the `Rotate now` button, or a
   *  post-counter bump from `composePost`). */
  readonly loadState: () => ChannelRotationState | Promise<ChannelRotationState>;
  /** Rotation policy. Static for the lifetime of the cron
   *  — if the user changes the cadence, stop + restart. */
  readonly policy: ChannelRotationPolicy;
  /** Per-rotation context — same shape as
   *  `runRotationOnce` takes. */
  readonly context: RotationContext;
  /** Optional error sink. The cron swallows thrown errors
   *  so a transient network failure does not kill the
   *  timer; this hook lets the UI surface them. */
  readonly onError?: (err: unknown) => void;
}

/**
 * Start a periodic rotation poller. Returns a handle whose
 * `.stop()` cancels the underlying `setInterval`. Safe to
 * leave running for the lifetime of a channel screen — one
 * `runRotationOnce` per `pollIntervalMs` is a cheap
 * heartbeat (zero network calls when the policy says no).
 */
export function startRotationCron(
  opts: RotationCronOptions,
): RotationCronHandle {
  const interval = Math.max(1_000, opts.pollIntervalMs ?? 5 * 60 * 1000);

  let stopped = false;

  const tick = async () => {
    if (stopped) return;
    try {
      const state = await Promise.resolve(opts.loadState());
      await runRotationOnce(opts.context, opts.policy, state, Date.now());
    } catch (err) {
      opts.onError?.(err);
    }
  };

  // Fire immediately on start so a tab that opens past the
  // rotation deadline catches up rather than waiting one
  // full `pollIntervalMs`. Errors swallowed identically.
  void tick();
  const handle = setInterval(() => {
    void tick();
  }, interval);

  return {
    stop() {
      if (stopped) return;
      stopped = true;
      clearInterval(handle);
    },
  };
}

// ---------------------------------------------------------
// helpers
// ---------------------------------------------------------

/** Parse a lowercase / mixed-case hex string into bytes.
 *  Returns `null` on any malformed input (odd length,
 *  non-hex char) — caller decides what to do. */
function hexToBytes(hex: string): Uint8Array | null {
  if (hex.length === 0 || hex.length % 2 !== 0) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; ++i) {
    const hi = hexNibble(hex.charCodeAt(2 * i));
    const lo = hexNibble(hex.charCodeAt(2 * i + 1));
    if (hi < 0 || lo < 0) return null;
    out[i] = (hi << 4) | lo;
  }
  return out;
}

function hexNibble(code: number): number {
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 97 && code <= 102) return 10 + code - 97;
  if (code >= 65 && code <= 70) return 10 + code - 65;
  return -1;
}

/** Standard base64 encoder for `Uint8Array`. Browsers
 *  expose `btoa(String.fromCharCode(...))` but that
 *  trips up on byte arrays longer than the call-stack
 *  limit; chunking keeps it stable for the 80-byte
 *  envelope (which is well under the limit anyway). */
function base64Encode(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; ++i) {
    const b = bytes[i];
    if (b === undefined) continue;
    s += String.fromCharCode(b);
  }
  return btoa(s);
}

/** Standard base64 of `bytes` (the entry's encoding). */
function b64Of(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin);
}
